import React, { useState, useEffect } from 'react';
import { Shield, Lock, CheckCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BetaGate({ children }) {
  const [betaEnabled, setBetaEnabled] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    // Check if already verified in this session
    const verified = sessionStorage.getItem('beta_verified');
    if (verified === 'true') {
      setHasAccess(true);
      setLoading(false);
      return;
    }

    // Check beta status
    try {
      const res = await fetch(`${API_URL}/api/beta/status`);
      const data = await res.json();
      setBetaEnabled(data.beta_enabled);
      
      if (!data.beta_enabled) {
        setHasAccess(true);
      }
    } catch (e) {
      // If can't check, assume beta is enabled
      setBetaEnabled(true);
    }
    setLoading(false);
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/beta/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });
      
      const data = await res.json();
      
      if (data.valid) {
        sessionStorage.setItem('beta_verified', 'true');
        setHasAccess(true);
      } else {
        setError(data.message || 'Code invalide');
      }
    } catch (e) {
      setError('Erreur de connexion. Réessayez.');
    }
    
    setVerifying(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  // Access granted
  if (hasAccess) {
    return children;
  }

  // Beta gate
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl mb-4">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">drbooking</h1>
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-200 px-4 py-2 rounded-full text-sm">
            <Lock className="w-4 h-4" />
            Accès sécurisé
          </div>
        </div>

        {/* Access Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">
            Accès Réservé
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Entrez votre code d'accès pour découvrir drbooking
          </p>

          <form onSubmit={verifyCode}>
            <div className="mb-4">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="CODE D'ACCÈS"
                className="w-full px-4 py-4 text-center text-xl font-mono tracking-widest border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                maxLength={20}
                autoFocus
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-center text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!code.trim() || verifying}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-teal-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {verifying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Vérification...
                </>
              ) : (
                <>
                  Accéder
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Pas de code ?{' '}
              <a href="mailto:contact@drbooking.pro" className="text-blue-600 hover:underline">
                Contactez-nous
              </a>
            </p>
          </div>
        </div>

        {/* Features Preview */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center text-white/80 text-sm">
          <div>
            <div className="text-2xl mb-1">🏥</div>
            <div>Médecins</div>
          </div>
          <div>
            <div className="text-2xl mb-1">📅</div>
            <div>Rendez-vous</div>
          </div>
          <div>
            <div className="text-2xl mb-1">🎤</div>
            <div>Assistant Vocal</div>
          </div>
        </div>

        <p className="text-center text-white/60 text-xs mt-8">
          © 2026 drbooking - Tous droits réservés
        </p>
      </div>
    </div>
  );
}
